import React, { useState, useEffect } from 'react';
import {
  Box,
  GridLegacy as Grid,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Typography,
  Card,
  CardContent,
  Alert,
  Slider,
  Stack,
  Chip,
  Divider,
  FormControlLabel,
  Switch,
  Autocomplete,
  CircularProgress,
  Avatar,
  ListItem,
  ListItemAvatar,
  ListItemText
} from '@mui/material';
import PersonIcon from '@mui/icons-material/Person';
import GroupIcon from '@mui/icons-material/Group';
import EmailIcon from '@mui/icons-material/Email';
import moment from 'moment';
import {
  DateTimePicker,
  LocalizationProvider
} from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { format, addWeeks, isValid } from 'date-fns';

// ✅ IMPORT API SERVICE
import { panelistAPI } from '../../services/api';

// ✅ Helper function to format date as local ISO string without UTC conversion
const formatLocalDateTime = (date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');
  const seconds = String(date.getSeconds()).padStart(2, '0');
  
  return `${year}-${month}-${day}T${hours}:${minutes}:${seconds}`;
};

// ✅ FIXED: Define apiService to resolve 'apiService is not defined' error
const apiService = {
  createAvailabilitySlots: (data) => panelistAPI.createAvailabilitySlots(data),
  createAvailabilitySlotsWithInvitations: (data) => panelistAPI.createAvailabilitySlotsWithInvitations(data),
  searchColleagues: (query) => panelistAPI.searchColleagues(query)
};

const AvailabilitySlotForm = ({
  initialData,
  onSubmit,
  onCancel,
  mode = 'create',
  userRole = 'panelist',
  panelists = []
}) => {
  // ===== FORM STATE =====
  const [formData, setFormData] = useState({
    slot_type: 'single',
    start_datetime: new Date(),
    end_datetime: new Date(new Date().getTime() + 60 * 60 * 1000), // +1 hour
    duration_minutes: 60,
    title: 'Available for Interview',
    description: '',
    location: '',
    meeting_type: 'video',
    meeting_link: '',
    recurring_weeks: 4,
    recurring_pattern: {},
    days_of_week: [],
    panelist_id: '',
    create_teams: false,
    meeting_subject: 'Available for Interview',
    // ✅ Enhanced Teams integration with invitations
    send_invitations: true,
    attendee_emails: [],
    auto_create_calendar_events: true
  });

  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [alert, setAlert] = useState({ open: false, message: '', severity: 'info' }); // ✅ Alert state

  // ✅ Colleague search and management state
  const [colleagueSearch, setColleagueSearch] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [searchTimeout, setSearchTimeout] = useState(null);

  // ===== INITIALIZE FORM =====
  useEffect(() => {
    if (initialData && mode === 'edit') {
      // EDIT MODE: Load existing slot data
      setFormData({
        slot_type: initialData.slot_type || 'single',
        start_datetime: new Date(initialData.start_datetime),
        end_datetime: new Date(initialData.end_datetime),
        duration_minutes: initialData.duration_minutes || 60,
        title: initialData.title || 'Available for Interview',
        description: initialData.description || '',
        location: initialData.location || '',
        meeting_type: initialData.meeting_type || 'video',
        meeting_link: initialData.meeting_link || '',
        recurring_weeks: 4,
        recurring_pattern: initialData.recurring_pattern || {},
        create_teams: initialData.create_teams || false,
        meeting_subject: initialData.meeting_subject || 'Available for Interview',
        // ✅ Load invitation settings
        send_invitations: initialData.send_invitations !== false,
        attendee_emails: initialData.attendee_emails || [],
        auto_create_calendar_events: initialData.auto_create_calendar_events !== false
      });
    } else if (initialData && mode === 'create') {
      // CREATE MODE: Handle pre-selected time slots from calendar
      const startDate = initialData.start_datetime ? new Date(initialData.start_datetime) : new Date();
      const endDate = initialData.end_datetime ? new Date(initialData.end_datetime) : new Date(startDate.getTime() + 60 * 60 * 1000);
      const durationMinutes = initialData.duration_minutes || Math.round((endDate.getTime() - startDate.getTime()) / (1000 * 60));

      console.log('🗓️ Pre-filling form with calendar selection:', {
        start: startDate,
        end: endDate,
        duration: durationMinutes
      });

      setFormData(prev => ({
        ...prev,
        start_datetime: startDate,
        end_datetime: endDate,
        duration_minutes: durationMinutes
      }));
    }
  }, [initialData, mode]);

  // ===== FORM HANDLERS =====
  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    
    // Clear error when field is updated
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }
  };

  // ===== ENHANCED DATE TIME CHANGE HANDLER =====
  const handleDateTimeChange = (field, newValue) => {
    if (newValue && isValid(newValue)) {
      setFormData(prev => {
        const updated = { ...prev, [field]: newValue };
        
        // Auto-adjust end time when start time changes
        if (field === 'start_datetime') {
          const durationMs = prev.duration_minutes * 60 * 1000;
          updated.end_datetime = new Date(newValue.getTime() + durationMs);
          
          console.log('📅 Auto-adjusted end time:', {
            start: newValue,
            end: updated.end_datetime,
            duration: prev.duration_minutes
          });
        }
        
        // Auto-adjust duration when end time changes
        if (field === 'end_datetime' && newValue > updated.start_datetime) {
          const durationMs = newValue.getTime() - updated.start_datetime.getTime();
          updated.duration_minutes = Math.round(durationMs / (1000 * 60));
          
          console.log('📅 Auto-adjusted duration:', updated.duration_minutes);
        }
        
        return updated;
      });
    }
  };

  // ===== ENHANCED DURATION CHANGE HANDLER =====
  const handleDurationChange = (event, newValue) => {
    const durationMinutes = newValue;
    
    setFormData(prev => {
      const updated = {
        ...prev,
        duration_minutes: durationMinutes,
        end_datetime: new Date(prev.start_datetime.getTime() + durationMinutes * 60 * 1000)
      };
      
      console.log('⏱️ Duration changed:', {
        duration: durationMinutes,
        newEndTime: updated.end_datetime
      });
      
      return updated;
    });
  };

  // ✅ FIXED: Colleague search functionality using API service
  const searchColleagues = async (searchTerm) => {
    if (!searchTerm || searchTerm.length < 2) {
      setSearchResults([]);
      return;
    }

    try {
      setSearchLoading(true);
      console.log('🔍 Searching colleagues:', searchTerm);
      
      // ✅ FIXED: Use panelistAPI with proper error handling
      const response = await panelistAPI.searchColleagues(searchTerm);
      
      if (response.data && response.data.status === 'success') {
        setSearchResults(response.data.colleagues || []);
        console.log('✅ Found colleagues:', response.data.colleagues?.length || 0);
      } else {
        console.error('❌ Failed to search colleagues:', response.data?.error);
        setSearchResults([]);
      }
    } catch (error) {
      console.error('❌ Colleague search error:', error);
      setSearchResults([]);
      
      // Show error alert for search issues
      setAlert({
        open: true,
        message: 'Failed to search colleagues. Please try again.',
        severity: 'warning'
      });
    } finally {
      setSearchLoading(false);
    }
  };

  // ✅ Debounced search handler
  const handleColleagueSearch = (searchTerm) => {
    setColleagueSearch(searchTerm);
    
    // Clear existing timeout
    if (searchTimeout) {
      clearTimeout(searchTimeout);
    }

    // Set new timeout for debounced search
    const newTimeout = setTimeout(() => {
      searchColleagues(searchTerm);
    }, 500); // 500ms delay

    setSearchTimeout(newTimeout);
  };

  // ✅ Add attendee email
  const addAttendeeEmail = (email) => {
    if (email && !formData.attendee_emails.includes(email)) {
      const updatedEmails = [...formData.attendee_emails, email];
      handleInputChange('attendee_emails', updatedEmails);
      setColleagueSearch(''); // Clear search
      setSearchResults([]); // Clear results
      console.log('✅ Added attendee:', email);
      
      // Clear any search errors
      if (errors.colleague_search) {
        setErrors(prev => ({
          ...prev,
          colleague_search: ''
        }));
      }
    }
  };

  // ✅ Remove attendee email
  const removeAttendeeEmail = (email) => {
    const updatedEmails = formData.attendee_emails.filter(e => e !== email);
    handleInputChange('attendee_emails', updatedEmails);
    console.log('❌ Removed attendee:', email);
  };

  // ✅ Add manual email
  const addManualEmail = () => {
    const email = colleagueSearch.trim();
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    
    if (emailRegex.test(email)) {
      addAttendeeEmail(email);
    } else {
      setErrors(prev => ({
        ...prev,
        colleague_search: 'Please enter a valid email address'
      }));
    }
  };

  // ===== ENHANCED VALIDATION =====
  const validateForm = () => {
    const newErrors = {};

    // Basic field validation
    if (!formData.title.trim()) {
      newErrors.title = 'Title is required';
    }

    if (!formData.start_datetime || !isValid(formData.start_datetime)) {
      newErrors.start_datetime = 'Valid start date/time is required';
    }

    if (!formData.end_datetime || !isValid(formData.end_datetime)) {
      newErrors.end_datetime = 'Valid end date/time is required';
    }

    // Time logic validation
    if (formData.start_datetime && formData.end_datetime) {
      if (formData.start_datetime >= formData.end_datetime) {
        newErrors.end_datetime = 'End time must be after start time';
      }

      // Check if start time is in the past (only for new slots)
      if (mode === 'create' && formData.start_datetime < new Date()) {
        newErrors.start_datetime = 'Start time cannot be in the past';
      }
    }

    // Duration validation
    if (formData.duration_minutes < 15) {
      newErrors.duration_minutes = 'Duration must be at least 15 minutes';
    }

    if (formData.duration_minutes > 480) { // 8 hours max
      newErrors.duration_minutes = 'Duration cannot exceed 8 hours';
    }

    // Recurring slots validation
    if (formData.slot_type === 'weekly_recurring') {
      if (formData.recurring_weeks < 1) {
        newErrors.recurring_weeks = 'Must create at least 1 week of slots';
      }
      if (formData.recurring_weeks > 52) {
        newErrors.recurring_weeks = 'Cannot create more than 52 weeks of slots';
      }
    }

    // Meeting type specific validation
    if (formData.meeting_type === 'video' && !formData.create_teams && !formData.meeting_link.trim()) {
      newErrors.meeting_link = 'Meeting link is required for video interviews (unless using Teams)';
    }

    // URL validation for meeting link
    if (formData.meeting_link.trim()) {
      const urlPattern = /^https?:\/\/.+/;
      if (!urlPattern.test(formData.meeting_link.trim())) {
        newErrors.meeting_link = 'Please enter a valid URL (starting with http:// or https://)';
      }
    }

    // ✅ Teams integration validation
    if (formData.create_teams && formData.send_invitations && formData.attendee_emails.length === 0) {
      newErrors.attendee_emails = 'Please add at least one attendee email when sending invitations';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // ✅ ENHANCED SUBMIT HANDLER - FIXED: Only call ONE endpoint, not both
  const handleSubmit = async () => {
    if (!validateForm()) {
      console.log('❌ Form validation failed:', errors);
      return;
    }

    setSubmitting(true);
    setAlert({ open: false, message: '', severity: 'info' });

    try {
      const submitData = {
        ...formData,
        // ✅ CRITICAL FIX: Format as local datetime without UTC conversion
        start_datetime: formatLocalDateTime(formData.start_datetime),
        end_datetime: formatLocalDateTime(formData.end_datetime),
        title: formData.title.trim(),
        description: formData.description.trim(),
        location: formData.location.trim(),
        meeting_link: formData.meeting_link.trim(),
        meeting_subject: formData.meeting_subject || 'Available for Interview',
        attendee_emails: formData.attendee_emails,
        send_invitations: formData.send_invitations && formData.create_teams,
        auto_create_calendar_events: formData.auto_create_calendar_events && formData.create_teams
      };

      console.log('📤 Submitting enhanced slot data:', submitData);

      let response;

      // ✅ FIXED: Choose ONE endpoint only
      if (formData.create_teams && formData.send_invitations && formData.attendee_emails.length > 0) {
        console.log('🎥 Using enhanced endpoint with Teams integration');
        response = await apiService.createAvailabilitySlotsWithInvitations(submitData);
      } else {
        console.log('📅 Using basic slot creation endpoint');
        response = await apiService.createAvailabilitySlots(submitData);
      }

      if (response && response.data && response.data.status === 'success') {
        console.log('✅ Slot created successfully:', response.data);
        
        setAlert({ 
          open: true, 
          message: response.data.message || 'Slot created successfully!',
          severity: 'success' 
        });
        
        // ✅ FIXED: Only call onSubmit ONCE, don't trigger secondary creation
        if (onSubmit) {
          // Don't await this to prevent double calls
          onSubmit(response.data);
        }
        
        // Reset form
        setTimeout(() => {
          setFormData({
            slot_type: 'single',
            start_datetime: new Date(),
            end_datetime: new Date(new Date().getTime() + 60 * 60 * 1000),
            duration_minutes: 60,
            title: 'Available for Interview',
            description: '',
            location: '',
            meeting_type: 'video',
            meeting_link: '',
            recurring_weeks: 4,
            recurring_pattern: {},
            days_of_week: [],
            panelist_id: '',
            create_teams: false,
            meeting_subject: 'Available for Interview',
            send_invitations: true,
            attendee_emails: [],
            auto_create_calendar_events: true
          });
          setErrors({});
        }, 2000);
        
      } else {
        throw new Error(response?.data?.error || 'Failed to create slot');
      }
      
    } catch (error) {
      console.error('❌ Form submission error:', error);
      
      let errorMessage = 'Failed to save slot. Please try again.';
      if (error.response?.status === 401) {
        errorMessage = 'Authentication required. Please log in again.';
      } else if (error.response?.data?.error) {
        errorMessage = error.response.data.error;
      }
      
      setErrors({ submit: errorMessage });
      setAlert({
        open: true,
        message: errorMessage,
        severity: 'error'
      });
    } finally {
      setSubmitting(false);
    }
  };

  // ===== UTILITY FUNCTIONS =====
  const formatPreviewTime = (date) => {
    if (!date || !isValid(date)) return 'Invalid Date';
    return format(date, 'MMM dd, yyyy HH:mm');
  };

  const getDurationText = (minutes) => {
    if (minutes < 60) return `${minutes} minutes`;
    const hours = Math.floor(minutes / 60);
    const remainingMinutes = minutes % 60;
    return remainingMinutes > 0 ? `${hours}h ${remainingMinutes}m` : `${hours} hour${hours > 1 ? 's' : ''}`;
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Box sx={{ p: 2 }}>
        {/* ===== SUCCESS/ERROR ALERTS ===== */}
        {alert.open && (
          <Alert 
            severity={alert.severity} 
            onClose={() => setAlert({ open: false, message: '', severity: 'info' })}
            sx={{ mb: 3 }}
          >
            {alert.message}
          </Alert>
        )}

        {/* ===== ERROR DISPLAY ===== */}
        {errors.submit && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {errors.submit}
          </Alert>
        )}

        {/* ===== SLOT TYPE SELECTION ===== */}
        <Card variant="outlined" sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              🗓️ Slot Configuration
            </Typography>
            
            <FormControl fullWidth sx={{ mb: 3 }}>
              <InputLabel>Slot Type</InputLabel>
              <Select
                value={formData.slot_type}
                onChange={(e) => handleInputChange('slot_type', e.target.value)}
                label="Slot Type"
                disabled={mode === 'edit'} // Can't change type when editing
              >
                <MenuItem value="single">Single Slot</MenuItem>
                <MenuItem value="weekly_recurring">Weekly Recurring</MenuItem>
              </Select>
            </FormControl>

            {formData.slot_type === 'weekly_recurring' && (
              <Alert severity="info" sx={{ mb: 2 }}>
                <strong>Recurring Slots:</strong> This will create {formData.recurring_weeks} identical slots, 
                one for each week starting from your selected date.
              </Alert>
            )}
          </CardContent>
        </Card>

        {/* ===== ADMIN PANELIST SELECTOR ===== */}
        {userRole === 'admin' && (
          <Card variant="outlined" sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                👤 Assign to Panelist (Admin Only)
              </Typography>
              
              <FormControl fullWidth required>
                <InputLabel>Select Panelist</InputLabel>
                <Select
                  value={formData.panelist_id || ''}
                  onChange={(e) => handleInputChange('panelist_id', e.target.value)}
                  label="Select Panelist"
                >
                  {panelists?.map(panelist => (
                    <MenuItem key={panelist.id} value={panelist.id}>
                      <Stack direction="row" spacing={2} alignItems="center">
                        <PersonIcon fontSize="small" />
                        <Box>
                          <Typography variant="body2">
                            {panelist.username}
                          </Typography>
                          <Typography variant="caption" color="textSecondary">
                            {panelist.email}
                          </Typography>
                        </Box>
                      </Stack>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <Alert severity="info" sx={{ mt: 2 }}>
                <Typography variant="body2">
                  <strong>Admin Feature:</strong> You are creating this availability slot for the selected panelist. 
                  They will be able to see and manage this slot in their calendar.
                </Typography>
              </Alert>
            </CardContent>
          </Card>
        )}

        {/* ===== ENHANCED RECURRING PATTERN FOR TEAM CALENDAR ===== */}
        {formData.slot_type === 'weekly_recurring' && (
          <Card variant="outlined" sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                🔄 Weekly Recurring Pattern
              </Typography>
              
              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    type="number"
                    label="Number of Weeks"
                    value={formData.recurring_weeks}
                    onChange={(e) => handleInputChange('recurring_weeks', parseInt(e.target.value) || 1)}
                    inputProps={{ min: 1, max: 52 }}
                    helperText="How many consecutive weeks (1-52)"
                  />
                </Grid>
                
                <Grid item xs={12} md={6}>
                  <FormControl fullWidth>
                    <InputLabel>Repeat Days</InputLabel>
                    <Select
                      multiple
                      value={formData.days_of_week || [moment(formData.start_datetime).day()]}
                      onChange={(e) => handleInputChange('days_of_week', e.target.value)}
                      label="Repeat Days"
                      renderValue={(selected) => 
                        selected.map(day => moment().day(day).format('ddd')).join(', ')
                      }
                    >
                      <MenuItem value={1}>Monday</MenuItem>
                      <MenuItem value={2}>Tuesday</MenuItem>
                      <MenuItem value={3}>Wednesday</MenuItem>
                      <MenuItem value={4}>Thursday</MenuItem>
                      <MenuItem value={5}>Friday</MenuItem>
                      <MenuItem value={6}>Saturday</MenuItem>
                      <MenuItem value={0}>Sunday</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
              </Grid>
              
              {/* Enhanced Team Calendar Preview */}
              <Alert severity="success" sx={{ mt: 2 }}>
                <Typography variant="body2" gutterBottom>
                  <strong>Team Calendar Impact:</strong>
                </Typography>
                <Typography variant="body2">
                  • Creates <strong>{formData.recurring_weeks}</strong> individual weekly slots
                  <br />
                  • Each appears as separate event: "{formData.start_datetime && moment(formData.start_datetime).format('dddd HH:mm')} - {formData.end_datetime && moment(formData.end_datetime).format('HH:mm')}"
                  <br />
                  • Visible to vendors for booking across all {formData.recurring_weeks} weeks
                  <br />
                  • Example: "12:00 PM Mon-Fri every week" creates 5 × {formData.recurring_weeks} = {5 * formData.recurring_weeks} individual time slots
                </Typography>
              </Alert>
            </CardContent>
          </Card>
        )}

        {/* ===== TIMING CONFIGURATION ===== */}
        <Card variant="outlined" sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              ⏰ Timing & Duration
            </Typography>
            
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <DateTimePicker
                  label="Start Date & Time"
                  value={formData.start_datetime}
                  onChange={(newValue) => handleDateTimeChange('start_datetime', newValue)}
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      error: !!errors.start_datetime,
                      helperText: errors.start_datetime
                    }
                  }}
                />
              </Grid>
              
              <Grid item xs={12} md={6}>
                <DateTimePicker
                  label="End Date & Time"
                  value={formData.end_datetime}
                  onChange={(newValue) => handleDateTimeChange('end_datetime', newValue)}
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      error: !!errors.end_datetime,
                      helperText: errors.end_datetime
                    }
                  }}
                />
              </Grid>
            </Grid>

            {/* ===== ENHANCED DURATION SLIDER ===== */}
            <Box sx={{ mt: 3 }}>
              <Typography gutterBottom>
                Duration: <strong>{getDurationText(formData.duration_minutes)}</strong>
              </Typography>
              <Slider
                value={formData.duration_minutes}
                onChange={handleDurationChange}
                step={15}
                marks={[
                  { value: 15, label: '15m' },
                  { value: 60, label: '1h' },
                  { value: 120, label: '2h' },
                  { value: 240, label: '4h' }
                ]}
                min={15}
                max={480} // 8 hours max
                valueLabelDisplay="auto"
                valueLabelFormat={(value) => getDurationText(value)}
                sx={{ mt: 2 }}
              />
              {errors.duration_minutes && (
                <Typography color="error" variant="caption">
                  {errors.duration_minutes}
                </Typography>
              )}
            </Box>

            {/* ===== RECURRING WEEKS CONFIGURATION ===== */}
            {formData.slot_type === 'weekly_recurring' && (
              <Box sx={{ mt: 3 }}>
                <TextField
                  fullWidth
                  type="number"
                  label="Number of Weeks"
                  value={formData.recurring_weeks}
                  onChange={(e) => handleInputChange('recurring_weeks', parseInt(e.target.value) || 1)}
                  inputProps={{ min: 1, max: 52 }}
                  error={!!errors.recurring_weeks}
                  helperText={errors.recurring_weeks || 'Create slots for how many consecutive weeks? (Max: 52)'}
                />
              </Box>
            )}
          </CardContent>
        </Card>

        {/* ===== SLOT DETAILS ===== */}
        <Card variant="outlined" sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              📝 Slot Details
            </Typography>
            
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Title"
                  value={formData.title}
                  onChange={(e) => handleInputChange('title', e.target.value)}
                  error={!!errors.title}
                  helperText={errors.title}
                  required
                  placeholder="e.g., Available for Technical Interview"
                />
              </Grid>
              
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  multiline
                  rows={3}
                  label="Description"
                  value={formData.description}
                  onChange={(e) => handleInputChange('description', e.target.value)}
                  placeholder="Optional description: interview focus, special requirements, etc."
                />
              </Grid>
              
              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <InputLabel>Meeting Type</InputLabel>
                  <Select
                    value={formData.meeting_type}
                    onChange={(e) => handleInputChange('meeting_type', e.target.value)}
                    label="Meeting Type"
                  >
                    <MenuItem value="video">🎥 Video Call</MenuItem>
                    <MenuItem value="phone">📞 Phone Call</MenuItem>
                    <MenuItem value="in_person">🏢 In Person</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Location"
                  value={formData.location}
                  onChange={(e) => handleInputChange('location', e.target.value)}
                  placeholder={
                    formData.meeting_type === 'video' 
                      ? 'e.g., Zoom Room A, Microsoft Teams' 
                      : formData.meeting_type === 'phone'
                      ? 'e.g., Conference Line, Direct Number'
                      : 'e.g., Office Building, Floor 2, Room 201'
                  }
                />
              </Grid>
              
              {formData.meeting_type === 'video' && !formData.create_teams && (
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Meeting Link"
                    value={formData.meeting_link}
                    onChange={(e) => handleInputChange('meeting_link', e.target.value)}
                    placeholder="https://zoom.us/j/123456789 or https://meet.google.com/abc-defg-hij"
                    error={!!errors.meeting_link}
                    helperText={errors.meeting_link}
                    required={!formData.create_teams}
                  />
                </Grid>
              )}
            </Grid>
          </CardContent>
        </Card>

        {/* ===== ✅ ENHANCED MICROSOFT TEAMS INTEGRATION WITH INVITATIONS ===== */}
        <Card variant="outlined" sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" color="primary" gutterBottom>
              🎥 Microsoft Teams Integration with Calendar Invitations
            </Typography>
            
            <FormControlLabel
              control={
                <Switch
                  checked={formData.create_teams}
                  onChange={(e) => handleInputChange('create_teams', e.target.checked)}
                  color="primary"
                />
              }
              label="Auto-create Teams meeting for this slot"
            />
            
            {formData.create_teams && (
              <Stack spacing={3} sx={{ mt: 2 }}>
                <TextField
                  fullWidth
                  label="Teams Meeting Subject"
                  value={formData.meeting_subject}
                  onChange={(e) => handleInputChange('meeting_subject', e.target.value)}
                  placeholder="e.g., Technical Interview Session"
                />

                {/* ✅ Calendar invitations toggle */}
                <FormControlLabel
                  control={
                    <Switch
                      checked={formData.send_invitations}
                      onChange={(e) => handleInputChange('send_invitations', e.target.checked)}
                      color="primary"
                    />
                  }
                  label="Send automatic calendar invitations to colleagues"
                />

                {/* ✅ Attendee management section */}
                {formData.send_invitations && (
                  <Box sx={{ mt: 2 }}>
                    <Typography variant="subtitle1" gutterBottom>
                      <GroupIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                      Invite Colleagues
                    </Typography>

                    {/* Colleague search */}
                    <Stack spacing={2}>
                      <Box sx={{ display: 'flex', gap: 1 }}>
                        <TextField
                          fullWidth
                          label="Search colleagues or enter email"
                          placeholder="Type name, email, or enter email address..."
                          value={colleagueSearch}
                          onChange={(e) => handleColleagueSearch(e.target.value)}
                          error={!!errors.colleague_search}
                          helperText={errors.colleague_search}
                          InputProps={{
                            endAdornment: searchLoading && <CircularProgress size={20} />
                          }}
                        />
                        <Button
                          variant="outlined"
                          onClick={addManualEmail}
                          disabled={!colleagueSearch.trim()}
                          sx={{ minWidth: 100 }}
                        >
                          Add
                        </Button>
                      </Box>

                      {/* Search results */}
                      {searchResults.length > 0 && (
                        <Card variant="outlined" sx={{ maxHeight: 200, overflow: 'auto' }}>
                          {searchResults.map((colleague, index) => (
                            <ListItem 
                              key={index} 
                              button 
                              onClick={() => addAttendeeEmail(colleague.email)}
                              disabled={formData.attendee_emails.includes(colleague.email)}
                            >
                              <ListItemAvatar>
                                <Avatar sx={{ bgcolor: 'primary.main' }}>
                                  {colleague.name?.charAt(0) || <PersonIcon />}
                                </Avatar>
                              </ListItemAvatar>
                              <ListItemText
                                primary={colleague.name}
                                secondary={
                                  <Stack>
                                    <Typography variant="caption" color="primary">
                                      <EmailIcon fontSize="inherit" sx={{ mr: 0.5 }} />
                                      {colleague.email}
                                    </Typography>
                                    {colleague.department && (
                                      <Typography variant="caption" color="textSecondary">
                                        {colleague.department} • {colleague.title}
                                      </Typography>
                                    )}
                                  </Stack>
                                }
                              />
                            </ListItem>
                          ))}
                        </Card>
                      )}

                      {/* Selected attendees */}
                      {formData.attendee_emails.length > 0 && (
                        <Box>
                          <Typography variant="subtitle2" gutterBottom>
                            Selected Attendees ({formData.attendee_emails.length}):
                          </Typography>
                          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                            {formData.attendee_emails.map((email, index) => (
                              <Chip
                                key={index}
                                label={email}
                                onDelete={() => removeAttendeeEmail(email)}
                                color="primary"
                                variant="outlined"
                                avatar={<Avatar><EmailIcon fontSize="small" /></Avatar>}
                              />
                            ))}
                          </Stack>
                        </Box>
                      )}

                      {errors.attendee_emails && (
                        <Alert severity="error">
                          {errors.attendee_emails}
                        </Alert>
                      )}
                    </Stack>
                  </Box>
                )}
                
                <Divider />

                <Alert severity="success">
                  <Typography variant="body2">
                    <strong>✅ Enhanced Teams Integration:</strong>
                    <br />
                    🎥 Teams meeting will be automatically created
                    <br />
                    📅 Calendar events will appear in colleagues' Outlook calendars
                    <br />
                    📧 Automatic email invitations will be sent
                    <br />
                    🔗 One-click join from calendar invitations
                    {formData.send_invitations && formData.attendee_emails.length > 0 && (
                      <>
                        <br />
                        <strong>📬 {formData.attendee_emails.length} colleagues will receive invitations</strong>
                      </>
                    )}
                  </Typography>
                </Alert>
              </Stack>
            )}
          </CardContent>
        </Card>

        {/* ===== ENHANCED PREVIEW FOR RECURRING SLOTS ===== */}
        {formData.slot_type === 'weekly_recurring' && formData.start_datetime && isValid(formData.start_datetime) && (
          <Card variant="outlined" sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                📅 Preview: Weekly Recurring Slots
              </Typography>
              
              <Stack spacing={1}>
                {Array.from({ length: Math.min(formData.recurring_weeks, 5) }).map((_, index) => {
                  const weekStart = addWeeks(formData.start_datetime, index);
                  const weekEnd = addWeeks(formData.end_datetime, index);
                  
                  return (
                    <Box 
                      key={index}
                      sx={{ 
                        p: 2, 
                        bgcolor: index === 0 ? '#e3f2fd' : '#f5f5f5', 
                        borderRadius: 1,
                        border: index === 0 ? '2px solid #2196f3' : '1px solid #e0e0e0'
                      }}
                    >
                      <Stack direction="row" spacing={2} alignItems="center">
                        <Chip 
                          label={`Week ${index + 1}`} 
                          color={index === 0 ? 'primary' : 'default'}
                          size="small"
                        />
                        <Typography variant="body2">
                          <strong>{formatPreviewTime(weekStart)}</strong> → {format(weekEnd, 'HH:mm')}
                        </Typography>
                        <Chip 
                          label={getDurationText(formData.duration_minutes)}
                          variant="outlined"
                          size="small"
                        />
                      </Stack>
                    </Box>
                  );
                })}
                
                {formData.recurring_weeks > 5 && (
                  <Box sx={{ p: 1, textAlign: 'center' }}>
                    <Typography variant="body2" color="textSecondary">
                      ... and <strong>{formData.recurring_weeks - 5} more weeks</strong>
                    </Typography>
                  </Box>
                )}
                
                <Divider sx={{ my: 2 }} />
                
                <Alert severity="success" sx={{ mt: 1 }}>
                  <strong>Total slots to create: {formData.recurring_weeks}</strong>
                  <br />
                  <Typography variant="body2" sx={{ mt: 1 }}>
                    Each slot will be available for vendor booking individually.
                    Duration: {getDurationText(formData.duration_minutes)} • 
                    Type: {formData.meeting_type} meeting
                    {formData.create_teams && (
                      <>
                        <br />
                        🎥 <strong>Teams meetings will be auto-created</strong> for each slot
                        {formData.send_invitations && formData.attendee_emails.length > 0 && (
                          <> with <strong>{formData.attendee_emails.length} invitations</strong> each</>
                        )}
                      </>
                    )}
                  </Typography>
                </Alert>
              </Stack>
            </CardContent>
          </Card>
        )}

        {/* ===== SINGLE SLOT PREVIEW ===== */}
        {formData.slot_type === 'single' && formData.start_datetime && isValid(formData.start_datetime) && (
          <Card variant="outlined" sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                👁️ Slot Preview
              </Typography>
              
              <Box sx={{ p: 2, bgcolor: '#e8f5e8', borderRadius: 1, border: '2px solid #4caf50' }}>
                <Stack spacing={1}>
                  <Typography variant="h6">
                    {formData.title}
                  </Typography>
                  <Typography variant="body1">
                    📅 <strong>{formatPreviewTime(formData.start_datetime)}</strong> → {format(formData.end_datetime, 'HH:mm')}
                  </Typography>
                  <Typography variant="body2">
                    ⏱️ Duration: {getDurationText(formData.duration_minutes)} • 
                    🎯 Type: {formData.meeting_type} meeting
                    {formData.create_teams && (
                      <>
                        <br />
                        🎥 <strong>Teams meeting:</strong> "{formData.meeting_subject}"
                        {formData.send_invitations && formData.attendee_emails.length > 0 && (
                          <>
                            <br />
                            📧 <strong>Invitations:</strong> {formData.attendee_emails.length} colleagues
                          </>
                        )}
                      </>
                    )}
                  </Typography>
                  {formData.description && (
                    <Typography variant="body2" color="textSecondary">
                      📝 {formData.description}
                    </Typography>
                  )}
                </Stack>
              </Box>
            </CardContent>
          </Card>
        )}

        {/* ===== FORM ACTIONS ===== */}
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mt: 3 }}>
          <Button 
            onClick={onCancel}
            size="large"
            disabled={submitting}
          >
            Cancel
          </Button>
          
          <Button
            variant="contained"
            onClick={handleSubmit}
            disabled={submitting}
            size="large"
            sx={{ minWidth: 200 }}
          >
            {submitting ? (
              <Stack direction="row" spacing={1} alignItems="center">
                <CircularProgress size={20} color="inherit" />
                <span>Processing...</span>
              </Stack>
            ) : (
              mode === 'edit' 
                ? 'Update Slot' 
                : formData.slot_type === 'weekly_recurring' 
                  ? `Create ${formData.recurring_weeks} Slots${formData.send_invitations && formData.attendee_emails.length > 0 ? ' + Send Invitations' : ''}`
                  : `Create Slot${formData.send_invitations && formData.attendee_emails.length > 0 ? ' + Send Invitations' : ''}`
            )}
          </Button>
        </Box>
      </Box>
    </LocalizationProvider>
  );
};

export default AvailabilitySlotForm;
